import { MaskedTextbox, SplitTextbox } from '@policygenius/mortar';
import React, { useState } from 'react';

import { FieldRenderProps } from 'forms/types';
import getErrorMessage from 'forms/utils/getErrorMessage';
import useEffectAfterMount from 'hooks/useEffectAfterMount';
import { inchesToFeet, feetToInches } from 'util/measurements';

import inputStyles from '../form-inputs.module.scss';
import { BaseHeightFieldProps, FeetAndInchesState, InputValue } from './types';

export type HeightFieldProps = BaseHeightFieldProps &
  FieldRenderProps<InputValue, HTMLInputElement, string>;

const HeightField: React.FC<HeightFieldProps> = ({
  id,
  input: { value: _value, ...input },
  meta,
  ...props
}) => {
  const [{ feetValue, inchesValue }, setFeetAndInches] = useState<FeetAndInchesState>(() => {
    const heightInInches = meta.initial;
    const { feet, inches } = inchesToFeet(heightInInches as number);

    return {
      feetValue: heightInInches ? feet : '',
      inchesValue: heightInInches ? inches : '',
    };
  });

  useEffectAfterMount(() => {
    if (feetValue === '' || inchesValue === '') {
      input.onChange(null);
    } else {
      input.onChange(feetToInches(feetValue, inchesValue));
    }
  }, [feetValue, inchesValue]);

  const onFeetChange: MaskedTextbox.Props['onChange'] = ({ unmasked }) =>
    setFeetAndInches((curr) => ({
      ...curr,
      feetValue: unmasked ? Number(unmasked) : '',
    }));

  const onInchesChange: MaskedTextbox.Props['onChange'] = ({ unmasked }) =>
    setFeetAndInches((curr) => ({
      ...curr,
      inchesValue: unmasked ? Number(unmasked) : '',
    }));

  const defaults = meta.initial ? inchesToFeet(meta.initial) : undefined;

  return (
    <div className={inputStyles['field-container']}>
      <SplitTextbox {...props} id={id ?? input.name} errorMessage={getErrorMessage(meta)}>
        <MaskedTextbox
          {...input}
          id="ft"
          type="text"
          name={`${input.name}Feet`}
          label="Feet"
          defaultValue={defaults?.feet}
          maskOptions={{
            mask: Number,
            validate: (value: string) => !value || (Number(value) >= 4 && Number(value) < 9),
          }}
          onChange={onFeetChange}
          suffix="ft"
          inputMode="numeric"
        />
        <MaskedTextbox
          {...input}
          id="in"
          type="text"
          name={`${input.name}Inches`}
          label="Inches"
          defaultValue={defaults?.inches}
          maskOptions={{
            mask: Number,
            validate: (value: string) => !value || (Number(value) >= 0 && Number(value) < 12),
          }}
          onChange={onInchesChange}
          suffix="in"
          inputMode="numeric"
        />
      </SplitTextbox>
    </div>
  );
};

export default HeightField;
